import { useDownloadFile } from '@newfront-insurance/admin-ui/files';
import { cn } from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { ArtifactPreviewPanel } from './artifact-preview-panel';
import type { ArtifactPreview, ArtifactPreviewMenuItem } from './types';
import { useChatOverlayContextSafe } from '../../../contexts/chat-overlay-context';
import { getFileExtension, NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../../../lib/analytics';

type ArtifactPreviewOwner = {
  items: ArtifactPreviewMenuItem[];
  order: number;
};

type ArtifactPreviewContextValue = {
  activeArtifactId: string | null;
  openArtifactPreview: (artifact: ArtifactPreviewMenuItem) => Promise<void>;
  previewingArtifactId: string | null;
  registerArtifacts: (ownerId: string, items: ArtifactPreviewMenuItem[]) => () => void;
};

interface ArtifactPreviewProviderProps {
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  panelClassName?: string;
}

const ArtifactPreviewContext = createContext<ArtifactPreviewContextValue | null>(null);

export function getArtifactPreviewMenuItemId({
  filename,
  messageUuid,
  source,
  uuid,
}: Pick<ArtifactPreviewMenuItem, 'filename' | 'messageUuid' | 'source' | 'uuid'>) {
  return uuid ? `file:${uuid}` : `file:${source}:${messageUuid ?? 'message'}:${filename}`;
}

export function useArtifactPreview() {
  const context = useContext(ArtifactPreviewContext);
  if (!context) {
    throw new Error('useArtifactPreview must be used inside ArtifactPreviewProvider');
  }

  return context;
}

export function useArtifactPreviewSafe() {
  return useContext(ArtifactPreviewContext);
}

export function ArtifactPreviewProvider({
  children,
  className,
  contentClassName,
  panelClassName,
}: ArtifactPreviewProviderProps) {
  const analytics = useAnalytics();
  const chatOverlayContext = useChatOverlayContextSafe();
  const { previewUrl } = useDownloadFile({});
  const [owners, setOwners] = useState<Map<string, ArtifactPreviewOwner>>(() => new Map());
  const [activeArtifactId, setActiveArtifactId] = useState<string | null>(null);
  const [activePreview, setActivePreview] = useState<ArtifactPreview | null>(null);
  const [isPreviewCollapsed, setIsPreviewCollapsed] = useState(false);
  const [previewingArtifactId, setPreviewingArtifactId] = useState<string | null>(null);
  const ownerOrderRef = useRef(new Map<string, number>());
  const nextOwnerOrderRef = useRef(0);
  const shouldRestoreSidebarOnCloseRef = useRef(false);

  const artifacts = useMemo(() => {
    const dedupedArtifacts = new Map<string, ArtifactPreviewMenuItem>();

    Array.from(owners.values())
      .sort((a, b) => a.order - b.order)
      .forEach(({ items }) => {
        items.forEach((item) => {
          if (!dedupedArtifacts.has(item.id)) {
            dedupedArtifacts.set(item.id, item);
          }
        });
      });

    return Array.from(dedupedArtifacts.values());
  }, [owners]);

  const closePreview = useCallback(() => {
    setActiveArtifactId(null);
    setActivePreview(null);
    setIsPreviewCollapsed(false);

    if (shouldRestoreSidebarOnCloseRef.current) {
      chatOverlayContext?.setIsSidebarOpen(true);
      shouldRestoreSidebarOnCloseRef.current = false;
    }
  }, [chatOverlayContext]);

  useEffect(() => {
    if (!activePreview) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      closePreview();
    };

    document.addEventListener('keydown', onKeyDown, { capture: true });

    return () => document.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [activePreview, closePreview]);

  const registerArtifacts = useCallback((ownerId: string, items: ArtifactPreviewMenuItem[]) => {
    let order = ownerOrderRef.current.get(ownerId);
    if (order === undefined) {
      order = nextOwnerOrderRef.current;
      nextOwnerOrderRef.current += 1;
      ownerOrderRef.current.set(ownerId, order);
    }

    setOwners((currentOwners) => {
      const nextOwners = new Map(currentOwners);
      nextOwners.set(ownerId, { items, order });
      return nextOwners;
    });

    return () => {
      setOwners((currentOwners) => {
        const nextOwners = new Map(currentOwners);
        nextOwners.delete(ownerId);
        return nextOwners;
      });
    };
  }, []);

  const openArtifactPreview = useCallback(
    async (artifact: ArtifactPreviewMenuItem) => {
      setPreviewingArtifactId(artifact.id);
      try {
        const fileUrl = await previewUrl(artifact.uuid);
        if (!fileUrl) return;

        trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.CHAT_FILE_DOWNLOADED, {
          fileExtension: getFileExtension(artifact.filename),
          fileUuid: artifact.uuid,
          messageUuid: artifact.messageUuid,
          source: `${artifact.source}_${artifact.providerId}_artifact_preview`,
        });

        if (chatOverlayContext?.isSidebarOpen) {
          shouldRestoreSidebarOnCloseRef.current = true;
          chatOverlayContext.setIsSidebarOpen(false);
        }
        if (chatOverlayContext && !chatOverlayContext.isStandaloneMode && !chatOverlayContext.isFullscreen) {
          chatOverlayContext.setIsFullscreen(true);
        }
        setActiveArtifactId(artifact.id);
        setIsPreviewCollapsed(false);
        setActivePreview({
          fileType: artifact.fileType,
          fileUrl,
          fileUuid: artifact.uuid,
          providerLabel: artifact.providerLabel,
          sourceUrl: fileUrl,
          title: artifact.filename,
        });
      } catch {
        return;
      } finally {
        setPreviewingArtifactId(null);
      }
    },
    [analytics, chatOverlayContext, previewUrl],
  );

  const contextValue = useMemo<ArtifactPreviewContextValue>(
    () => ({
      activeArtifactId,
      openArtifactPreview,
      previewingArtifactId,
      registerArtifacts,
    }),
    [activeArtifactId, openArtifactPreview, previewingArtifactId, registerArtifacts],
  );

  return (
    <ArtifactPreviewContext.Provider value={contextValue}>
      <div className={cn('relative flex min-h-0 min-w-0 flex-1 overflow-hidden', className)}>
        <div className={cn('flex min-w-0 flex-1 flex-col', contentClassName)}>{children}</div>
        <ArtifactPreviewPanel
          activeArtifactId={activeArtifactId}
          artifacts={artifacts}
          className={panelClassName}
          isCollapsed={isPreviewCollapsed}
          onClose={closePreview}
          onCollapseChange={setIsPreviewCollapsed}
          onOpenArtifact={(artifact) => {
            void openArtifactPreview(artifact);
          }}
          preview={activePreview}
          previewingArtifactId={previewingArtifactId}
        />
      </div>
    </ArtifactPreviewContext.Provider>
  );
}
